import React, { useContext, useEffect, useState } from "react";
import GeaoVerticalStepperLayout from "../../shared/Layout/GeaoVerticalStepperLayout";
import PrincipalFlowVerticalStepperElements from "../../../constants/PrincipalFlowVerticalStepperElements";
import { classes } from "../../../../../util/style-utils";
import { useForm } from "react-hook-form";
import FileUploadForm from "../../../../FileUpload/FileUploadForm";
import { FileUploadState } from "../../../../FileUpload/FileUploadState";
import { yupResolver } from "@hookform/resolvers/yup";
import { GeaoContext } from "../../../GeaoContextProvider";
import useTitle from "../../../../../hooks/useTitle";
import useAgentRosterInitializer from "../../../hooks/useAgentRosterInitializer";
import { GdsIcon } from '@gds/web-components/react';
import useInterviewUpdater from "../../../hooks/useInterviewUpdater";
import useMappers from "./useMappers";
import useNavigation from "./useNavigation";
import useFormButtons from "../../../hooks/useFormButtons";
import { validationSchema } from "./validation";
import FileUploadErrorMessage from "../../../../FileUpload/FileUploadErrorMessage";

export default function PrincipalAgencyEmployeeInformation() {
    useTitle("Employee Information");

    const { interviewIsCompleted } = useContext(GeaoContext);

    const [formData, setFormData] = useState();

    const { defaultValues, agencyAnswers } = useMappers(formData);

    const { register, formState: { errors }, handleSubmit, trigger, getValues, watch } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues
    });

    const {
        pageStatus,
        exitAfterSave,
        onSubmit,
        onSaveAndExit
    } = useFormButtons(setFormData, trigger, getValues);

    const activityRequest = exitAfterSave ? {
        ActivityCategory: "GEAO_ONBOARDING",
        ActivitySubType: "SAVE_AND_EXIT",
        SourceApplication: "AGIWUI",
        AdditionalData: {
            ComponentName: "PrincipalAgencyEmployeeInformation"
        }
    } : null;

    const {
        updatingInterviews,
        interviewsUpdated
    } = useInterviewUpdater(
        "PrincipalAgencyEmployeeInformation",
        pageStatus,
        null,
        agencyAnswers,
        activityRequest
    );

    useNavigation(interviewsUpdated, exitAfterSave);

    const {
        initializeFromCsv,
        progress: agentRosterProgress,
        errors: agentRosterErrors
    } = useAgentRosterInitializer();

    const watchSoldLobOther = watch("currentLinesOfBusinessOther");

    useEffect(() => { register("soldLinesOfBusiness") }, []);

    const [fileUploadState, setFileUploadState] = useState(FileUploadState.Upload);
    const [submissionErrorAlertContent, setSubmissionErrorAlertContent] = useState();

    function onFileUploadSubmit(selectedFiles) {
        initializeFromCsv(selectedFiles[0]).then(_ => {
            FileUploadErrorMessage({ agentRosterErrors, setFileUploadState, setSubmissionErrorAlertContent });
        });
    }

    const fileUploadParameters = {
        allowedFileExtensions: [".csv"],
        allowInvalidFileTypes: true,
        minFileSize: 0,
        maxFileSize: 25600,
        maxCombinedFileSize: 25600,
        maxFileCount: 1,
        combinedFileSize: 0
    };

    function numberInputOnly(element) {
        const numberRegex = new RegExp("[0-9]");
        const otherRegex = new RegExp("[^0-9]");
        if (element) {
            element.addEventListener("beforeinput", (event) => {
                if (event.data !== null && !numberRegex.test(event.data)) {
                    event.preventDefault();
                }
            })
            element.addEventListener("paste", (event) => {
                const text = event.clipboardData.getData("Text")
                if (event.data !== null && otherRegex.test(text)) {
                    event.preventDefault();
                }
            })
        }
    }

    useEffect(() => {
        numberInputOnly(document.getElementById("total-employees"));
        numberInputOnly(document.getElementById("unlicensed-employees"));
        numberInputOnly(document.getElementById("personalLinesDirectCarrierAppointments"));
        numberInputOnly(document.getElementById("licensedAgentsPersonalLines"));
        numberInputOnly(document.getElementById("commercialLinesDirectCarrierAppointments"));
        numberInputOnly(document.getElementById("licensedAgentsCommercialLines"));
        numberInputOnly(document.getElementById("licensedAgentsCombination"));
        numberInputOnly(document.getElementById("licensedAgentsOtherLines"));
    }, [])

    return (
        <GeaoVerticalStepperLayout
            verticalStepperElements={PrincipalFlowVerticalStepperElements}
            currentStepperItem="Employee Information"
            header="Employee Information"
        >
            <form className="form geao-form" onSubmit={handleSubmit(onSubmit)}>
                <h3 className="margin-bottom">Agent Appointments</h3>

                <fieldset className={classes("form-field checkbox-group", errors.soldLinesOfBusiness && "form-field--error")} role="radiogroup">
                    <legend>
                        Current Lines of Business Sold{/*
                        */}<span className='required'>*</span>
                    </legend>
                    <span className='form-descriptive-copy'>Select all options that apply.</span>
                    <div className="checkbox-wrapper col-2">
                        <div>
                            <input id="personal-auto" type="checkbox" {...register("currentLinesOfBusinessPersonal")} />
                            <label htmlFor="personal-auto" className="checkbox"><span>Personal Auto</span></label>
                        </div>
                        <div>
                            <input id="commercial" type="checkbox" {...register("currentLinesOfBusinessCommercial")} />
                            <label htmlFor="commercial" className="checkbox">Commercial</label>
                        </div>
                        <div>
                            <input id="home" type="checkbox" {...register("currentLinesOfBusinessHome")} />
                            <label htmlFor="home" className="checkbox">Home</label>
                        </div>
                        <div>
                            <input id="health" type="checkbox" {...register("currentLinesOfBusinessHealth")} />
                            <label htmlFor="health" className="checkbox">Health</label>
                        </div>
                        <div>
                            <input id="other" type="checkbox" {...register("currentLinesOfBusinessOther")} />
                            <label htmlFor="other" className="checkbox">Other</label>
                        </div>
                        {errors.soldLinesOfBusiness &&
                            <span className="form-message">{errors.soldLinesOfBusiness.message}</span>
                        }
                    </div>
                </fieldset>

                {watchSoldLobOther &&
                    <div className="flex-row">
                        <div className={classes("form-field", errors.currentLinesOfBusinessOtherText && "form-field--error")}>
                            <label htmlFor="lob-other-text" className="text">
                                Other{/*
                                */}<span className='required'>*</span>
                            </label>
                            <input
                                id="lob-other-text"
                                type="text"
                                {...register("currentLinesOfBusinessOtherText")}
                            />
                            {errors.currentLinesOfBusinessOtherText &&
                                <span className="form-message">{errors.currentLinesOfBusinessOtherText.message}</span>
                            }
                        </div>
                    </div>
                }

                <div className="flex-row label-row">
                    <div className="form-field">
                        <label htmlFor="total-employees" className="text">
                            Total Number of Employees{/*
                            */}<span className='required'>*</span>
                        </label>
                        <span className="form-descriptive-copy">Including the Principal</span>
                    </div>
                    <div className="form-field">
                        <label htmlFor="unlicensed-employees" className="text">
                            Number of Unlicensed Employees{/*
                            */}<span className='required'>*</span>
                        </label>
                    </div>
                </div>

                <div className="flex-row">
                    <div className={classes("form-field", errors.totalEmployees && "form-field--error")}>
                        <input
                            id="total-employees"
                            type="text"
                            {...register("totalEmployees")}
                        />
                        {errors.totalEmployees &&
                            <span className="form-message">{errors.totalEmployees.message}</span>
                        }
                    </div>

                    <div className={classes("form-field", errors.unlicensedAgents && "form-field--error")}>
                        <input
                            id="unlicensed-employees"
                            type="text"
                            {...register("unlicensedAgents")}
                        />
                        {errors.unlicensedAgents &&
                            <span className="form-message">{errors.unlicensedAgents.message}</span>
                        }
                    </div>
                </div>

                <div className="flex-row label-row">
                    <div className="form-field">
                        <label htmlFor="personalLinesDirectCarrierAppointments" className="text">
                            Number of Direct Carrier Appointments for <u>Personal Lines</u>{/*
                            */}<span className='required'>*</span>
                        </label>
                    </div>
                    <div className="form-field">
                        <label htmlFor="licensedAgentsPersonalLines" className="text">
                            Number of Producers Selling <br /><u>Personal Lines</u>{/*
                            */}<span className='required'>*</span>
                        </label>
                    </div>
                </div>

                <div className="flex-row">
                    <div className={classes("form-field", errors.personalLinesDirectCarrierAppointments && "form-field--error")}>
                        <input
                            id="personalLinesDirectCarrierAppointments"
                            type="text"
                            {...register("personalLinesDirectCarrierAppointments")}
                        />
                        {errors.personalLinesDirectCarrierAppointments &&
                            <span className="form-message">{errors.personalLinesDirectCarrierAppointments.message}</span>
                        }
                    </div>

                    <div className={classes("form-field", errors.licensedAgentsPersonalLines && "form-field--error")}>
                        <input
                            id="licensedAgentsPersonalLines"
                            type="text"
                            {...register("licensedAgentsPersonalLines")}
                        />
                        {errors.licensedAgentsPersonalLines &&
                            <span className="form-message">{errors.licensedAgentsPersonalLines.message}</span>
                        }
                    </div>
                </div>

                <div className="flex-row label-row">
                    <div className="form-field">
                        <label htmlFor="commercialLinesDirectCarrierAppointments" className="text">
                            Number of Direct Carrier Appointments for <u>Commercial Lines</u>{/*
                            */}<span className='required'>*</span>
                        </label>
                    </div>
                    <div className="form-field">
                        <label htmlFor="licensedAgentsCommercialLines" className="text">
                            Number of Producers Selling <br /><u>Commercial Lines</u>{/*
                            */}<span className='required'>*</span>
                        </label>
                    </div>
                </div>

                <div className="flex-row">
                    <div className={classes("form-field", errors.commercialLinesDirectCarrierAppointments && "form-field--error")}>
                        <input
                            id="commercialLinesDirectCarrierAppointments"
                            type="text"
                            {...register("commercialLinesDirectCarrierAppointments")}
                        />
                        {errors.commercialLinesDirectCarrierAppointments &&
                            <span className="form-message">{errors.commercialLinesDirectCarrierAppointments.message}</span>
                        }
                    </div>

                    <div className={classes("form-field", errors.licensedAgentsCommercialLines && "form-field--error")}>
                        <input
                            id="licensedAgentsCommercialLines"
                            type="text"
                            {...register("licensedAgentsCommercialLines")}
                        />
                        {errors.licensedAgentsCommercialLines &&
                            <span className="form-message">{errors.licensedAgentsCommercialLines.message}</span>
                        }
                    </div>
                </div>

                <div className="flex-row">
                    <div className={classes("form-field", errors.licensedAgentsCombination && "form-field--error")}>
                        <label htmlFor="licensedAgentsCombination" className="text">
                            Number of Producers Selling <u>Combination of Personal and Commercial Lines</u>{/*
                            */}<span className='required'>*</span>
                        </label>
                        <input
                            id="licensedAgentsCombination"
                            type="text"
                            {...register("licensedAgentsCombination")}
                        />
                        {errors.licensedAgentsCombination &&
                            <span className="form-message">{errors.licensedAgentsCombination.message}</span>
                        }
                    </div>
                </div>

                <div className="flex-row">
                    <div className={classes("form-field", errors.licensedAgentsOtherLines && "form-field--error")}>
                        <label htmlFor="licensedAgentsOtherLines" className="text">
                            Number of Producers Selling Other Lines of Insurance{/*
                            */}<span className='required'>*</span>
                        </label>
                        <span className="form-descriptive-copy">Lines Other Than Personal or Commercial</span>
                        <input
                            id="licensedAgentsOtherLines"
                            type="text"
                            {...register("licensedAgentsOtherLines")}
                        />
                        {errors.licensedAgentsOtherLines &&
                            <span className="form-message">{errors.licensedAgentsOtherLines.message}</span>
                        }
                    </div>
                </div>

                <h3>Agent Roster</h3>

                <p>
                    Upload your agent roster using our <b>Agent Roster Template</b> below.
                    We'll use this to email invitations to your associates to begin their application process.
                </p>

                <ol className="list list--numbered bold-markers">
                    <li>
                        <strong>Download</strong> the Agent Roster Template. Using another template can lead to upload errors.
                    </li>
                    <li>
                        <strong>Complete</strong> the roster and save it as a .csv file.
                        <ul className="square-markers">
                            <li>
                                <span>
                                    List the names and business emails for all associates employed by the agency.
                                </span>
                            </li>
                            <li>
                                <u>Do not</u> include the agency principal/owner on the list.
                            </li>
                        </ul>
                    </li>
                    <li>
                        <strong>Upload</strong> the completed roster.
                    </li>
                    <li>
                        <strong>Submit</strong> the roster.
                    </li>
                </ol>

                <div>
                    <a href="geao/agent-roster.csv" target="_blank">
                        <button type="button" className="btn btn--primary btn--full-mobile">
                            <span>Download Agent Roster Template</span>
                        </button>
                    </a>
                </div>

                <strong>Upload only one .csv file. Max 25MB.</strong>

                <FileUploadForm
                    fileUploadState={fileUploadState}
                    setFileUploadState={setFileUploadState}
                    onFileUploadSubmit={onFileUploadSubmit}
                    fileUploadParameters={fileUploadParameters}
                    progress={agentRosterProgress}
                    displayParameters={{
                        submittingTopText: <h3>Submitting...</h3>,
                        submittingSubtext: null,
                        completeTopText: <h3>File Submitted</h3>,
                        uploadHeader: <h4 className="margin-bottom">File Ready to Submit</h4>,
                        submittingHeader: null,
                        completeHeader: <h4 className="margin-bottom">Submitted File</h4>,
                        errorHeader: <h4 className="margin-bottom">Failed Upload</h4>,
                        strokeSeparator: false,
                        bottomSubmitButton: true,
                        selectedFilesHeaderElement: "h4",
                        submitButtonText: "Submit",
                        submissionErrorAlertContent
                    }}
                />

                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                        justifyContent: 'left',
                        marginBottom: '30px'
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'left', gap: '10px' }}>
                        <GdsIcon
                            color="yellow"
                            iconType="repair-shop"
                            size="medium"
                        />
                        <strong>Need to make a change after files are submitted?</strong>
                    </div>
                    <span style={{ marginLeft: '41px' }}>
                        You can delete your file by selecting the trash can icon. Then update your Agent Roster file, save, upload, and resubmit.
                    </span>
                </div>

                <p>
                    <span className='required'>*</span> Indicates required field.
                </p>

                <div className="margin-top">
                    <button
                        type="submit"
                        className="btn btn--primary btn--full-mobile btn--pull-right"
                        disabled={updatingInterviews || interviewIsCompleted}
                    >
                        <span>Next</span>
                    </button>

                    <button
                        type="button"
                        className="btn btn--secondary btn--full-mobile btn--pull-right"
                        disabled={updatingInterviews || interviewIsCompleted}
                        onClick={onSaveAndExit}
                    >
                        <span>Save And Exit</span>
                    </button>
                </div>
            </form>
        </GeaoVerticalStepperLayout>
    );
}